import { AddressType } from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import {
    PoolButton,
    PoolModal,
    PoolSelect,
    PoolTextField,
    ScrollToFieldError,
    SelectOption,
} from '@synergy/components';

import { AddressAdditionalInfo } from './address-additional-info';
import { AddressFormValues } from '../../models/address-form-values';
import { AddressMainData } from './address-main-data';
import { addressModalValidationSchema } from './address-modal-validation-schema';

interface AddressModalProps {
    address: AddressFormValues | null;
    hasAdditionalInfos: boolean;
    requireRegion: boolean;
    onClose: () => void;
    onAddressSubmit: (values: AddressFormValues) => void;
}

export const AddressModal = (props: AddressModalProps) => {
    const { address, onClose, onAddressSubmit, requireRegion } = props;

    const addressTypeOptions: SelectOption[] = [
        {
            value: AddressType.Work,
            displayValue: <FormattedMessage id="app.common.work" />,
        },
        {
            value: AddressType.Private,
            displayValue: <FormattedMessage id="app.common.private" />,
        },
    ];

    const getActionChildren = (formikProps: FormikProps<AddressFormValues>) => {
        return [
            <PoolButton key="1" styleType="firstLevel" variant="text" onClick={onClose}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="2" styleType="firstLevel" type="submit" onClick={() => formikProps.submitForm()}>
                <FormattedMessage id={address?.id && address.id > 0 ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>,
        ];
    };

    const handleAddressTypeChange = (form: FormikProps<AddressFormValues>, name: string) => (value?: string) => {
        if (value) {
            form.setFieldValue(name, value);
        }
    };

    if (!address) {
        return null;
    }

    return (
        <Formik
            initialValues={address}
            onSubmit={onAddressSubmit}
            validationSchema={addressModalValidationSchema(requireRegion)}
        >
            {formikProps => (
                <PoolModal
                    title={
                        <FormattedMessage
                            id={
                                address?.id && address.id > 0
                                    ? 'app.contactsAndOrganizations.detail.editAddress'
                                    : 'app.contactsAndOrganizations.detail.addAddress'
                            }
                        />
                    }
                    isVisible={!!address}
                    actionChildren={getActionChildren(formikProps)}
                    handleClose={onClose}
                >
                    <ScrollToFieldError />
                    <Grid container={true} spacing={5}>
                        <Grid container={true} item={true} spacing={2}>
                            <Grid item={true} xs={12}>
                                <Field name="title">
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            label={<FormattedMessage id="app.label.title" />}
                                            fullWidth={true}
                                            error={!!meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                            {...field}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name="type">
                                    {({ field, form }: FieldProps<AddressType>) => (
                                        <PoolSelect
                                            label={<FormattedMessage id="app.label.addressType" />}
                                            fullWidth={true}
                                            options={addressTypeOptions}
                                            disallowDefaultValue={true}
                                            onValueChange={handleAddressTypeChange(form, field.name)}
                                            value={field.value}
                                            name={field.name}
                                            noSorting={true}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>

                        {props.hasAdditionalInfos && <AddressAdditionalInfo />}
                        <AddressMainData requireRegion={requireRegion} />
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
